import React, { ComponentProps } from 'react';
import { Button, Form } from 'antd';

import EventsSelect from '@src/emp-3.0/components/EventsSelect';
import { EEventType } from '@src/emp-3.0/types';
import useFieldRequiredStatus from '@src/pages/workflow/hooks/useFieldRequiredStatus';
import CustomFormItem from '@src/emp-3.0/components/Metrics/IndividualMetric/MetricBuilder/CustomFormItem';
import { useFlowStore } from '@src/pages/workflow/store/flowBuilder';
import { IFlowEventsProps } from './typed';
import EventItem from './EventItem';
import { EventLabel } from '@src/emp-3.0/components/Metrics/components';
import PairingLogicSelector from './PairingLogic';

const FlowEvents: React.FC<IFlowEventsProps> = ({ form, isView }) => {
  // get setInitialEvent, setCompleteEvent,  from useFlowStore
  const { initialEvent, setInitialEvent, setCompleteEvent, completeEvent, setActiveTool } =
    useFlowStore();
  const initialEventId = Form.useWatch(['definition', 'events', 'start_event'], form);
  const completeEventId = Form.useWatch(['definition', 'events', 'success_event'], form);

  const { status: startEventStatus, enableRequired } = useFieldRequiredStatus([
    'definition',
    'events',
    'start_event',
  ]);
  const { status: successEventStatus } = useFieldRequiredStatus([
    'definition',
    'events',
    'success_event',
  ]);

  const onEventChange = (_, type, event) => {
    const eventType = typeof event === 'string' ? 'raw' : 'mapped';
    if (type === EEventType.START_EVENT) {
      setInitialEvent(null);
    } else if (type === EEventType.SUCCESS_EVENT) {
      setCompleteEvent(null);
    }
    form.setFieldsValue({
      definition: { events: { [type]: eventType === 'mapped' ? event.event_id : event } },
    });
  };

  return (
    <>
      <div id={`${EEventType.START_EVENT}`}>
        <CustomFormItem
          name={['definition', 'events', 'start_event']}
          dependencies={[
            ['definition', 'events', 'success_event'],
            ['definition', 'events', 'error_event'],
            ['definition', 'events', 'without_event'],
          ]}
          label={
            <EventLabel
              prefix={null}
              tooltip="The initial event defines the flow start, such that clicking the login or checkout button"
              text="Initial Event"
            />
          }
          labelAlign="left"
          labelCol={{ span: 6 }}
          validateStatus={startEventStatus}
          rules={
            [
              enableRequired && { required: true, message: 'Initial Event is required' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const successEvent = getFieldValue(['definition', 'events', 'success_event']);
                  const errorEvent = getFieldValue(['definition', 'events', 'error_event']);

                  if (!value || !(successEvent === value || errorEvent?.includes(value))) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error(
                      'Initial Event cannot be the same as Complete Event or Error Event or Without Event',
                    ),
                  );
                },
              }),
            ].filter(Boolean) as ComponentProps<typeof Form.Item>['rules']
          }
        >
          {isView ? (
            <div />
          ) : (
            <EventsSelect
              eventType={EEventType.START_EVENT}
              onChange={(value, event) => {
                if (value) {
                  onEventChange(value, EEventType.START_EVENT, event);
                }
              }}
            ></EventsSelect>
          )}
        </CustomFormItem>
        <EventItem
          style={{ marginLeft: '28px' }}
          eventId={initialEventId}
          event={initialEvent}
          type={EEventType.START_EVENT}
          viewModeProps={{
            onEdit: (event) => {
              setInitialEvent(event);
            },
            onDelete: () => {
              setInitialEvent(null);
              form.setFieldsValue({
                definition: { events: { start_event: null } },
              });
              form.validateFields([
                ['definition', 'events', 'success_event'],
                ['definition', 'events', 'error_event'],
                ['definition', 'events', 'without_event'],
              ]);
            },
            onClone: (event) => {
              setInitialEvent(event);
            },
          }}
          editModeProps={{
            onSave: (event) => {
              form.setFieldsValue({
                definition: { events: { start_event: event.event_id } },
              });
              setTimeout(() => {
                setInitialEvent(null);
              }, 100);
            },
            onCancel: () => {
              setInitialEvent(null);
            },
            onChange: (event) => {
              if (Number(event)) {
                form.setFieldsValue({
                  definition: { events: { start_event: event } },
                });
                form.validateFields([
                  ['definition', 'events', 'success_event'],
                  ['definition', 'events', 'error_event'],
                  ['definition', 'events', 'without_event'],
                ]);

                setTimeout(() => {
                  setInitialEvent(null);
                }, 100);
              } else {
                setInitialEvent(event);
              }
            },
          }}
        />
      </div>
      <div style={{ height: '16px' }}></div>
      <div id={`${EEventType.SUCCESS_EVENT}`}>
        <CustomFormItem
          name={['definition', 'events', 'success_event']}
          dependencies={[
            ['definition', 'events', 'start_event'],
            ['definition', 'events', 'error_event'],
            ['definition', 'events', 'without_event'],
          ]}
          label={
            <EventLabel
              prefix={null}
              tooltip="The complete event defines the flow end, such that displaying the homepage or confirming payment complete"
              text="Complete Event"
            />
          }
          labelAlign="left"
          labelCol={{ span: 6 }}
          validateStatus={successEventStatus}
          rules={
            [
              enableRequired && { required: true, message: 'Complete Event is required' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const startEvent = getFieldValue(['definition', 'events', 'start_event']);
                  const errorEvent = getFieldValue(['definition', 'events', 'error_event']);
                  if (!value || !(startEvent === value || errorEvent?.includes(value))) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error(
                      'Complete Event cannot be the same as Initial Event or Error Event or Without Event',
                    ),
                  );
                },
              }),
            ].filter(Boolean) as ComponentProps<typeof Form.Item>['rules']
          }
        >
          {isView ? (
            <div />
          ) : (
            <EventsSelect
              eventType={EEventType.SUCCESS_EVENT}
              onChange={(value, event) => {
                onEventChange(value, EEventType.SUCCESS_EVENT, event);
              }}
            ></EventsSelect>
          )}
        </CustomFormItem>
        <EventItem
          style={{ marginLeft: '28px' }}
          type={EEventType.SUCCESS_EVENT}
          event={completeEvent}
          eventId={completeEventId}
          viewModeProps={{
            onEdit: (event) => {
              setCompleteEvent(event);
            },
            onDelete: () => {
              setCompleteEvent(null);
              form.setFieldsValue({
                definition: { events: { success_event: undefined } },
              });
              form.validateFields([
                ['definition', 'events', 'start_event'],
                ['definition', 'events', 'error_event'],
                ['definition', 'events', 'without_event'],
              ]);
            },
            onClone: (event) => {
              setCompleteEvent(event);
            },
          }}
          editModeProps={{
            onSave: (event) => {
              form.setFieldsValue({
                definition: { events: { success_event: event.event_id } },
              });
              setTimeout(() => {
                setCompleteEvent(null);
              }, 100);
            },
            onCancel: () => {
              setCompleteEvent(null);
            },
            onChange: (event) => {
              if (Number(event)) {
                form.setFieldsValue({
                  definition: { events: { success_event: event } },
                });
                form.validateFields([
                  ['definition', 'events', 'start_event'],
                  ['definition', 'events', 'error_event'],
                  ['definition', 'events', 'without_event'],
                ]);
                setTimeout(() => {
                  setCompleteEvent(null);
                }, 100);
              } else {
                setCompleteEvent(event);
              }
            },
          }}
        />
      </div>
      <Form.Item noStyle name={['definition', 'pairing_logic']}>
        <PairingLogicSelector isView={isView} />
      </Form.Item>
      {isView ? null : (
        <div
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          <Button
            type={'link'}
            onClick={() => {
              setActiveTool('validation');
            }}
            disabled={!initialEventId && !completeEventId}
          >
            Validate Event Coverage
          </Button>
        </div>
      )}
    </>
  );
};

export default FlowEvents;
